import React, { useCallback, useEffect, useState } from 'react';
import styles from './BillingSettings.module.scss';
import {
    DataList,
    DataListTypes,
    ListItem,
} from '../../../components/dataList/DataList';
import {
    setAuthNetModalOpen,
    setAutopayAccount,
    setAutopayCustomerId,
    setAutopayLocationId,
    setAutopayModalOpen,
    setAutopayStatus,
    setNavilineModalOpen,
    setOrganizationId,
    setUserId,
    useAppStateBillingSettingsSelector,
} from './billingSettingsSlice';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    addPaymentInformation,
    loadPaymentInformation,
    loadPaymentProfile,
    removePaymentInformation,
    removePaymentMethod,
} from './billingSettingsActionCreators';
import { AddCardModal } from './AddCardModal';
import { AutopayConfigModal } from './AutopayConfigModal/AutopayConfigModal';
import { openMessageModal } from '../../../components/statusModal/messageModalActionCreators';
import { MessageModal } from '../../../components/statusModal/MessageModal';
import { axios } from '../../../services/axiosService';
import { useFeatureFlag } from '../../featureFlags/FeatureFlag';
import { NavilineAddModal } from './NavilineAddModal';
import { useHistory, useLocation } from 'react-router-dom';

export interface BillingSettingsProps {
    UserId?: string;
    OrganizationId?: string;
}

// Component for displaying both the user's naviline accounts and the stored payments
export function BillingSettings({
    UserId,
    OrganizationId,
}: BillingSettingsProps) {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const history = useHistory();
    const location = useLocation();
    const [autopayAcctEnabled, setAutopayAcctEnabled] = useState(false);
    const [autopayAcctCard, setAutopayAcctCard] = useState();
    const [autopayId, setAutopayId] = useState();
    const [deletedCardId, setDeletedCardId] = useState('');
    const [deleteCardWarningOpen, setDeleteCardWarningOpen] = useState(false);
    const [deleteAutopayModalOpen, setDeleteAutopayModalOpen] = useState(false);
    const [paymentMethods, setPaymentMethods] = useState<ListItem[]>([]);
    const coreFF = useFeatureFlag('EnableCORE');

    const {
        navilineModalOpen,
        navilineListData,
        authNetModalOpen,
        paymentProfile,
        autopayModalOpen,
        coreStoredPayments,
        autopayCustomerId,
        autopayLocationId,
        loadingStoredPayments,
    } = useAppStateBillingSettingsSelector((state) => state);

    const handleAutoOpenAutopayModal = useCallback((_id: string, data: any, scrollToSection: boolean = false) => {
        if (scrollToSection) {
            const el = document.getElementById('manage-autopay-section');
            if (el) {
                el.scrollIntoView({ behavior: 'smooth' });
            }
        }

        setAutopayAcctEnabled(!!data.Autopay?.AutopayEnabled);
        setAutopayAcctCard(data.Autopay?.AutopayProfileId as any);
        setAutopayId((data.Autopay as any)?.AutopayId);
        dispatch(setAutopayAccount(_id));
        dispatch(setAutopayCustomerId(data.CustomerId));
        dispatch(setAutopayLocationId(data.LocationId));
        dispatch(setAutopayStatus(data.Autopay?.AutopayStatus));
        if (data.Autopay?.AutopayStatus === 'C') {
            setAutopayAcctEnabled(true);
        }
        
        // Small delay to allow scrolling to complete before opening modal
        setTimeout(() => {
            dispatch(setAutopayModalOpen(true));
            // Clear the hash to prevent reopening when modal is closed
            history.replace(location.pathname);
        }, 300);
    }, [dispatch, history, location.pathname, setAutopayAcctCard, setAutopayAcctEnabled, setAutopayId]);

    useEffect(() => {
        dispatch(setUserId(UserId));
        dispatch(setOrganizationId(OrganizationId));
        dispatch(loadPaymentInformation());
        dispatch(loadPaymentProfile(UserId, coreFF?.enabled));
    }, [dispatch, UserId, OrganizationId, coreFF]);

    // Auto-open autopay modal when directed to manage-autopay-section with single account
    useEffect(() => {
        if (
            location.hash === '#manage-autopay-section' &&
            navilineListData.length === 1 &&
            !loadingStoredPayments &&
            !autopayModalOpen
        ) {
            handleAutoOpenAutopayModal(navilineListData[0].Id, navilineListData[0].Data, true);
        }
    }, [navilineListData, loadingStoredPayments, autopayModalOpen, location.hash, handleAutoOpenAutopayModal]);

    useEffect(() => {
        if (coreFF) {
            let resultingPaymentMethods;
            if (coreFF?.enabled) {
                resultingPaymentMethods = coreStoredPayments
                    ? coreStoredPayments.map((paymentMethod) => {
                          return {
                              Id: paymentMethod.id,
                              Label: `${
                                  paymentMethod.name
                                      ? paymentMethod.name + ' - '
                                      : ''
                              } ${paymentMethod.card_type} - ${
                                  paymentMethod.accountnum
                              }`,
                              Data: paymentMethod,
                              Tag: undefined,
                          };
                      })
                    : [];
            } else {
                resultingPaymentMethods = paymentProfile
                    ? paymentProfile.map((profile: any) => {
                          return {
                              Id: profile.Id,
                              Label: `${profile.Type} - ${
                                  profile.MaskedAccountNumber
                              }${
                                  profile.ProfileDescription
                                      ? ` (${profile.ProfileDescription})`
                                      : ''
                              }`,
                              Data: profile,
                              Tag: undefined,
                          };
                      })
                    : [];
            }
            setPaymentMethods(resultingPaymentMethods);
        }
    }, [coreFF, coreStoredPayments, paymentProfile, loadingStoredPayments]);

    const removeCard = async (id: string) => {
        const result = (await axios.get(`/payment-methods/${id}`)).data;
        setDeletedCardId(id);
        setDeleteCardWarningOpen(true);
        // Show warning if card is attached to autopay
        if (result) {
            dispatch(
                openMessageModal(
                    'warning-delete-saved-card-with-autopay',
                    'warning',
                ),
            );
        } else {
            dispatch(openMessageModal('warning-delete-saved-card', 'warning'));
        }
    };

    return (
        <div>
            <h1>{t('Billing.BillingSettings')}</h1>
            <p className={styles.achDisclaimer}>
                {t('Billing.BillingSettingsDesc')}
            </p>
            <h2>{t('Billing.ManageCreditCardAutopay')}</h2>

            <NavilineAddModal
                modalOpen={navilineModalOpen}
                onClose={(accountNumber, streetNumber) =>
                    accountNumber && streetNumber
                        ? dispatch(
                              addPaymentInformation(
                                  accountNumber,
                                  streetNumber,
                                  navilineListData,
                              ),
                          )
                        : dispatch(setNavilineModalOpen(false))
                }
            />
            <AddCardModal modalOpen={authNetModalOpen} />
            <AutopayConfigModal
                modalOpen={autopayModalOpen}
                enabled={autopayAcctEnabled}
                savedCard={autopayAcctCard}
                id={autopayId}
            />
            {/* Remove naviline account modal */}
            <MessageModal
                modalOpen={deleteAutopayModalOpen}
                onConfirmationButtonClick={() => {
                    dispatch(
                        removePaymentInformation(
                            autopayCustomerId,
                            autopayLocationId,
                        ),
                    );
                    setDeleteAutopayModalOpen(false);
                }}
                onCloseButtonClick={() => {
                    setDeleteAutopayModalOpen(false);
                }}
            />
            {/* Delete saved payment modal */}
            <MessageModal
                modalOpen={deleteCardWarningOpen}
                onConfirmationButtonClick={() => {
                    setDeleteCardWarningOpen(false);
                    dispatch(
                        removePaymentMethod(
                            deletedCardId,
                            coreFF?.enabled,
                            UserId!,
                        ),
                    );
                }}
                onCloseButtonClick={() => {
                    setDeleteCardWarningOpen(false);
                }}
            />

            {/* Autopay and bills */}
            <DataList
                title={t('Billing.WaterBillingAccount', {
                    count: navilineListData.length,
                })}
                type={DataListTypes.navilineAccounts}
                items={navilineListData}
                addCallback={() => dispatch(setNavilineModalOpen(true))}
                deleteCallback={(_id, data) => {
                    setDeleteAutopayModalOpen(true);
                    dispatch(
                        openMessageModal('warning-remove-autopay', 'warning'),
                    );
                    dispatch(setAutopayCustomerId(data.CustomerId));
                    dispatch(setAutopayLocationId(data.LocationId));
                }}
                tagClickCallback={(_id, data) => {
                    handleAutoOpenAutopayModal(_id, data);
                }}
            />

            {/* Stored payments */}
            <DataList
                title={t('Billing.StoredPaymentMethod', {
                    count: paymentMethods.length,
                })}
                type={DataListTypes.savedPaymentMethods}
                items={paymentMethods}
                addCallback={() =>
                    coreFF?.enabled
                        ? history.push('/add-stored-payment')
                        : dispatch(setAuthNetModalOpen(true))
                }
                deleteCallback={(_id, data) => removeCard(_id)}
            />
        </div>
    );
}
